import { useFragment } from "@apollo/client"
import { useMemo } from "react"
import {
  CohortPermissionsFragment,
  CohortPermissionsFragmentDoc,
  useCohortPermissionsQuery
} from "@pathwright/graphql-client/src/generated"
import {
  hasEditorLevelAccess,
  hasLearnerLevelAccess,
  hasModeratorLevelAccess,
  hasObserverLevelAccess,
  hasTeacherLevelAccess
} from "../group/permissions"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canAdministrate } from "../user/permissions"

export function useCohortPermissionsFragment(cohortId: number) {
  const fragment = useFragment<CohortPermissionsFragment>({
    fragment: CohortPermissionsFragmentDoc,
    fragmentName: "CohortPermissions",
    from: {
      __typename: "Group",
      id: cohortId
    }
  })

  return fragment.complete ? fragment.data : null
}

export type CohortPermissions = {
  hasEditorLevelAccess: boolean | null
  hasTeacherLevelAccess: boolean | null
  hasModeratorLevelAccess: boolean | null
  hasLearnerLevelAccess: boolean | null
  hasObserverLevelAccess: boolean | null
}

const useCohortPermissions = ({
  cohortId
}: {
  cohortId?: number | null
}): CohortPermissions => {
  const pwContext = usePathwrightContext()

  // Falling back to fragment if possible (avoid extra network request).
  const cohortFragment = useCohortPermissionsFragment(cohortId!)

  const cohortPermissionsQuery = useCohortPermissionsQuery({
    variables: {
      cohortId: cohortId!
    },
    skip: !cohortId || !!cohortFragment || canAdministrate(pwContext),
    notifyOnNetworkStatusChange: true
  })

  const cohort = cohortPermissionsQuery.data?.cohort || cohortFragment
  const sourceCohort = cohort?.source || cohort

  return useMemo(() => {
    const hasAdminLevelAccess = canAdministrate(pwContext)
    // NOTE: a value of null indicates to the consumer that the permission
    // query is still loading
    const defaultPerm = cohortPermissionsQuery.loading ? null : false

    return {
      hasEditorLevelAccess:
        hasAdminLevelAccess ||
        hasEditorLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasTeacherLevelAccess:
        hasAdminLevelAccess ||
        hasTeacherLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasModeratorLevelAccess:
        hasAdminLevelAccess ||
        hasModeratorLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasLearnerLevelAccess:
        hasAdminLevelAccess ||
        hasLearnerLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm,
      hasObserverLevelAccess:
        hasAdminLevelAccess ||
        hasObserverLevelAccess(pwContext, cohort || sourceCohort) ||
        defaultPerm
    }
  }, [cohortPermissionsQuery.loading, pwContext, cohort, sourceCohort])
}

export default useCohortPermissions
